import React from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/HeroSection';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import CallToAction from '../components/CallToAction';
import AiForBusiness from '../components/AiForBusiness';
import ServiceArea from '../components/ServiceArea';
import useSeo from '../hooks/useSeo';
import { process, services } from '../data/site';

const reasons = [
  {
    title: 'One team, full stack',
    text: 'Hardware, firmware, cloud, apps and AI are designed together, so nothing gets lost between vendors.',
  },
  {
    title: 'Prototypes, not slide decks',
    text: 'We build working hardware early so you can test the real thing with real users and real data.',
  },
  {
    title: 'Secure by design',
    text: 'Encryption, secure boot, safe update paths and least-privilege access are part of the plan from day one.',
  },
  {
    title: 'You own everything',
    text: 'Schematics, source code, documentation and credentials are yours, and your team can maintain what we deliver.',
  },
];

const Home = () => {
  useSeo('/');

  return (
    <>
      <Hero />

      {/* Services overview */}
      <section className="border-t border-ink-700 py-24">
        <div className="container-site">
          <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="eyebrow">What we do</p>
              <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold text-white md:text-4xl">
                Everything it takes to ship a connected product.
              </h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold text-signal-400 hover:text-signal-300">
              All services <Icon name="arrow" className="h-4 w-4" />
            </Link>
          </Reveal>

          <div className="mt-14 grid gap-px overflow-hidden rounded-xl border border-ink-700 bg-ink-700 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((service, i) => (
              <Reveal key={service.id} delay={(i % 3) * 0.08} className="bg-ink-950">
                <Link
                  to={`/services#${service.id}`}
                  className="group flex h-full flex-col p-8 transition hover:bg-ink-900"
                >
                  <span className="text-signal-400">
                    <Icon name={service.icon} className="h-7 w-7" />
                  </span>
                  <h3 className="mt-6 font-display text-lg font-semibold text-white">{service.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{service.summary}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 transition group-hover:text-signal-400">
                    Learn more <Icon name="arrow" className="h-3.5 w-3.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <AiForBusiness />

      {/* Process */}
      <section className="border-t border-ink-700 bg-ink-900/50 py-24">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow">How we work</p>
            <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold text-white md:text-4xl">
              From idea to working product, one clear step at a time.
            </h2>
          </Reveal>

          <ol className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {process.map((item, i) => (
              <Reveal as="li" key={item.step} delay={i * 0.08} className="relative border-t border-ink-600 pt-6">
                <span className="absolute -top-px left-0 h-px w-12 bg-signal-400" />
                <span className="font-mono text-sm text-signal-400">{item.step}</span>
                <h3 className="mt-3 font-display text-xl font-semibold text-white">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{item.text}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Why us */}
      <section className="border-t border-ink-700 py-24">
        <div className="container-site grid gap-14 lg:grid-cols-[1fr_1.4fr]">
          <Reveal>
            <p className="eyebrow">Why Gomagen Labs</p>
            <h2 className="mt-4 font-display text-3xl font-bold text-white md:text-4xl">
              A team that builds things, not just advises on them.
            </h2>
            <p className="mt-6 leading-relaxed text-slate-400">
              Most connected products fail at the seams between hardware, software and data. We work across all
              of them, so the whole system is designed to work together from the first prototype.
            </p>
          </Reveal>

          <div className="grid gap-6 sm:grid-cols-2">
            {reasons.map((reason, i) => (
              <Reveal key={reason.title} delay={i * 0.08} className="card p-6">
                <span className="grid h-8 w-8 place-items-center rounded-md bg-signal-400/10 text-signal-400">
                  <Icon name="check" className="h-4 w-4" />
                </span>
                <h3 className="mt-5 font-display font-semibold text-white">{reason.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{reason.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ServiceArea />

      <CallToAction />
    </>
  );
};

export default Home;
